import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  alpha, useTheme, CircularProgress, Chip, ToggleButton, ToggleButtonGroup,
  LinearProgress, IconButton, Paper,
} from '@mui/material';
import {
  Download, UploadFile, Replay, Close as CloseIcon, CloudUpload, TaskAlt,
  ErrorOutline, CheckCircle,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import expenseService, { BulkRowsValidationError } from '../../services/expenseService';
import expenseCategoryService from '../../services/expenseCategoryService';
import type { ExpenseCategory, ExpenseSubCategory } from '../../types';
import {
  buildBulkExpensesXlsx,
  parseBulkExpensesXlsx,
  toBulkRowsPayload,
  buildLookups,
  type ParsedBulkExpenseRow,
} from '../../utils/bulkExpensesXlsx';
import { downloadXlsxBlob } from '../../utils/xlsx';
import BulkUploadReviewTable from './BulkUploadReviewTable';
import CopyableIdCell from '../common/CopyableIdCell';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { useTimezone } from '../../hooks/useTimezone';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Stage = 'pick' | 'review' | 'result';

const BULK_UPLOAD_CAP = 500;

const BulkUploadDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [stage, setStage] = useState<Stage>('pick');
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [subCategories, setSubCategories] = useState<ExpenseSubCategory[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [buildingTemplate, setBuildingTemplate] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedBulkExpenseRow[]>([]);
  const [filter, setFilter] = useState<'all' | 'ok' | 'error'>('all');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ batchId: string; created: number } | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const { getToday } = useTimezone();
  const lookups = useMemo(
    () => buildLookups(categories, subCategories, getToday()),
    [categories, subCategories, getToday],
  );

  // Load the catalog once when the dialog opens. Cached per-open so edits
  // to the school's categories mid-session reflect on next open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(null);
    Promise.all([
      expenseCategoryService.getCategories(),
      expenseCategoryService.getAllSubCategories(),
    ])
      .then(([cats, subs]) => {
        if (cancelled) return;
        setCategories(cats);
        setSubCategories(subs);
      })
      .catch((err) => {
        if (cancelled) return;
        setCatalogError(err?.message || 'Failed to load categories');
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const resetFlow = useCallback(() => {
    setStage('pick');
    setRows([]);
    setFilter('all');
    setResult(null);
    setUploadedFileName(null);
  }, []);

  const closeImmediately = () => {
    resetFlow();
    onClose();
  };

  const handleClose = () => {
    if (submitting) return;
    // Confirm before throwing away review state so a stray Esc / backdrop
    // click doesn't nuke the user's edits.
    if (stage === 'review' && rows.length > 0) {
      setDiscardConfirmOpen(true);
      return;
    }
    closeImmediately();
  };

  const onDownloadTemplate = async () => {
    try {
      setBuildingTemplate(true);
      const blob = await buildBulkExpensesXlsx({ categories, subCategories });
      const today = new Date().toISOString().slice(0, 10);
      downloadXlsxBlob(`expense-template-${today}.xlsx`, blob);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to build template', { variant: 'error' });
    } finally {
      setBuildingTemplate(false);
    }
  };

  const handleFile = useCallback(async (file: File) => {
    setParsing(true);
    try {
      const parsed = await parseBulkExpensesXlsx(file, lookups);
      if (parsed.fileError) {
        enqueueSnackbar(parsed.fileError, { variant: 'error' });
        return;
      }
      if (parsed.rows.length === 0) {
        enqueueSnackbar('No data rows found in the file.', { variant: 'warning' });
        return;
      }
      if (parsed.rows.length > BULK_UPLOAD_CAP) {
        enqueueSnackbar(
          `This file has ${parsed.rows.length} rows — the limit is ${BULK_UPLOAD_CAP} per upload. Split it and try again.`,
          { variant: 'error' },
        );
        return;
      }
      setRows(parsed.rows);
      setUploadedFileName(file.name);
      setFilter('all');
      setStage('review');
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to read the file', { variant: 'error' });
    } finally {
      setParsing(false);
    }
  }, [lookups, enqueueSnackbar]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => { if (accepted[0]) handleFile(accepted[0]); },
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: false,
    disabled: catalogLoading || !!catalogError || parsing,
  });

  const okCount = rows.filter((r) => r.clientStatus === 'ok').length;
  const errorCount = rows.length - okCount;

  const onSubmit = async () => {
    const payload = toBulkRowsPayload(rows);
    if (payload.length === 0) {
      enqueueSnackbar('No ready-to-upload rows. Fix the errors or remove them.', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await expenseService.bulkCreateRows(payload);
      setResult({ batchId: res.batchId, created: res.created });
      setStage('result');
      onSuccess();
    } catch (err: any) {
      if (err instanceof BulkRowsValidationError) {
        // Backend caught something the client missed. Re-mark the rows so
        // the user can see which ones and why without re-doing anything.
        const errorByRow = new Map(err.errors.map((e) => [e.rowNumber, e.errorMessage]));
        setRows((prev) => prev.map((r) =>
          errorByRow.has(r.rowNumber)
            ? { ...r, clientStatus: 'error', clientError: errorByRow.get(r.rowNumber) }
            : r,
        ));
        setFilter('error');
        enqueueSnackbar(
          `${err.errors.length} row(s) failed server-side validation. Fix the highlighted rows and resubmit.`,
          { variant: 'error' },
        );
      } else {
        enqueueSnackbar(err?.message || 'Bulk upload failed', { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const renderPickStage = () => (
    <Box>
      {/* Elegant explainer / rules banner */}
      <Paper
        elevation={0}
        sx={{
          p: 2, mb: 3, borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Download the template below — category and sub-category columns are
          pre-filled with dropdowns of this school's live catalog. Fill in up
          to {BULK_UPLOAD_CAP} rows, drop the file back here, and you'll
          review every row (and fix any errors) before anything is submitted.
        </Typography>
      </Paper>

      {catalogError ? (
        <Paper
          elevation={0}
          sx={{
            p: 2, mb: 3, borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.05),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          }}
        >
          <Typography variant="body2" color="error.main">
            {catalogError}
          </Typography>
        </Paper>
      ) : null}

      {/* Step 1 — download */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
          Step 1
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={buildingTemplate ? <CircularProgress size={16} /> : <Download />}
            onClick={onDownloadTemplate}
            disabled={catalogLoading || !!catalogError || buildingTemplate}
            sx={{ textTransform: 'none' }}
          >
            {buildingTemplate ? 'Preparing…' : 'Download template'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {catalogLoading
              ? 'Loading catalog…'
              : `${categories.length} categor${categories.length === 1 ? 'y' : 'ies'} · ${subCategories.length} sub-categor${subCategories.length === 1 ? 'y' : 'ies'}`}
          </Typography>
        </Box>
      </Box>

      {/* Step 2 — upload */}
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
          Step 2
        </Typography>
        <Box
          {...getRootProps()}
          sx={{
            mt: 1, p: 4, borderRadius: 2, textAlign: 'center',
            cursor: catalogLoading || !!catalogError || parsing ? 'not-allowed' : 'pointer',
            border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
            bgcolor: isDragActive
              ? alpha(theme.palette.primary.main, 0.06)
              : alpha(theme.palette.background.default, 0.5),
            transition: 'all 120ms ease',
            '&:hover': {
              borderColor: catalogLoading || !!catalogError || parsing
                ? theme.palette.divider
                : theme.palette.primary.main,
              bgcolor: catalogLoading || !!catalogError || parsing
                ? alpha(theme.palette.background.default, 0.5)
                : alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          <input {...getInputProps()} />
          {parsing ? (
            <>
              <CircularProgress size={28} sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Reading your file…
              </Typography>
            </>
          ) : (
            <>
              <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" fontWeight={500}>
                {isDragActive ? 'Drop the file here' : 'Drag an XLSX file here or click to browse'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Max {BULK_UPLOAD_CAP} rows per upload
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderReviewStage = () => (
    <Box>
      {/* Summary bar */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          p: 2, mb: 2, borderRadius: 2,
          bgcolor: alpha(theme.palette.background.default, 0.6),
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <UploadFile fontSize="small" sx={{ color: 'text.secondary' }} />
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography variant="body2" fontWeight={500}>
            {uploadedFileName ?? 'Uploaded file'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {rows.length} row{rows.length === 1 ? '' : 's'} parsed
          </Typography>
        </Box>
        <Chip
          size="small"
          icon={<CheckCircle sx={{ fontSize: 16 }} />}
          label={`${okCount} ready`}
          sx={{
            bgcolor: alpha(theme.palette.success.main, 0.1),
            color: theme.palette.success.main, fontWeight: 600,
          }}
        />
        {errorCount > 0 && (
          <Chip
            size="small"
            icon={<ErrorOutline sx={{ fontSize: 16 }} />}
            label={`${errorCount} need${errorCount === 1 ? 's' : ''} fixing`}
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main, fontWeight: 600,
            }}
          />
        )}
        <Button
          size="small" startIcon={<Replay />} onClick={resetFlow}
          sx={{ textTransform: 'none' }}
        >
          Start over
        </Button>
      </Box>

      <ToggleButtonGroup
        value={filter}
        exclusive
        size="small"
        onChange={(_, v) => v && setFilter(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="all" sx={{ textTransform: 'none' }}>
          All ({rows.length})
        </ToggleButton>
        <ToggleButton value="ok" sx={{ textTransform: 'none' }}>
          Ready ({okCount})
        </ToggleButton>
        <ToggleButton value="error" sx={{ textTransform: 'none' }} disabled={errorCount === 0}>
          Errors ({errorCount})
        </ToggleButton>
      </ToggleButtonGroup>

      {submitting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <BulkUploadReviewTable
        rows={rows}
        categories={categories}
        subCategories={subCategories}
        lookups={lookups}
        filter={filter}
        onChange={setRows}
      />
    </Box>
  );

  const renderResultStage = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <TaskAlt sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {result?.created} expense{result?.created === 1 ? '' : 's'} uploaded
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        They're in the pending queue, ready for approval.
      </Typography>
      {result && (
        <Box
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            px: 2, py: 1, borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Batch:
          </Typography>
          <CopyableIdCell value={result.batchId} fontWeight={400} truncate />
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flex: 1 }}>
          Bulk upload expenses
          <Typography variant="body2" color="text.secondary" fontWeight={400}>
            {stage === 'pick' && 'Download the template, fill it, and upload.'}
            {stage === 'review' && 'Review every row — fix errors or edit before submitting.'}
            {stage === 'result' && 'Done.'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {stage === 'pick' && renderPickStage()}
        {stage === 'review' && renderReviewStage()}
        {stage === 'result' && renderResultStage()}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {stage === 'review' && (
          <>
            <Button onClick={handleClose} color="inherit" disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
              onClick={onSubmit}
              disabled={submitting || okCount === 0}
            >
              {submitting
                ? 'Uploading…'
                : `Submit ${okCount} expense${okCount === 1 ? '' : 's'}`}
            </Button>
          </>
        )}
        {stage === 'result' && (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        )}
      </DialogActions>

      <ConfirmationDialog
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={() => { setDiscardConfirmOpen(false); closeImmediately(); }}
        title="Discard this upload?"
        message="Your edits to the review table will be lost. The uploaded file is not saved anywhere."
        confirmText="Discard"
        cancelText="Keep editing"
        confirmColor="error"
      />
    </Dialog>
  );
};

export default BulkUploadDialog;
